import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * Un errore di rendering in una pagina non deve lasciare lo schermo bianco
 * (R-75): al suo posto un messaggio nella lingua del sito, con intestazione e
 * piè di pagina ancora utilizzabili. `resetKey` (il percorso) azzera l'errore
 * quando si cambia pagina.
 */
interface Props {
  children: ReactNode;
  resetKey?: string;
  /** Cosa mostrare al posto dei figli in errore (predefinito: il messaggio di pagina). `null` = niente. */
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
  key?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, key: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.key) return { error: null, key: props.resetKey };
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Solo il tipo e lo stack dei componenti: nessun dato dell'utente.
    console.error("render_error", error.name, info.componentStack);
  }

  render() {
    if (this.state.error) return this.props.fallback !== undefined ? this.props.fallback : <ErrorFallback />;
    return this.props.children;
  }
}

export function ErrorFallback() {
  const { t } = useTranslation();
  return (
    <div role="alert" style={{ padding: "96px 24px", maxWidth: 520, margin: "0 auto", textAlign: "center" }}>
      <h1 style={{ fontSize: 28, fontWeight: 500, margin: "0 0 12px" }}>{t("common.errorPageTitle")}</h1>
      <p style={{ fontSize: 15, lineHeight: 1.6, opacity: 0.8, margin: "0 0 28px" }}>{t("common.errorPageBody")}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          background: "#1C4B3B",
          color: "#fff",
          border: "none",
          padding: "12px 28px",
          borderRadius: 999,
          fontSize: 14,
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        {t("common.reload")}
      </button>
    </div>
  );
}
